import React, { useEffect, useState } from 'react';

import {
  Checkbox, FormControlLabel, Grid, useMediaQuery, useTheme,
} from '@mui/material';
import _ from 'lodash';
import {
  CartesianGrid,
  Cell,
  Label,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import {
  CircleDot, StarDot, TriangleDot,
} from '../../../components/ChartComponents';
import ContentCard from '../../../components/ContentCard';
import { NoContentMessage } from '../../../components/PageLayout';
import SkeletonStack from '../../../components/SkeletonStack';
import { useGetExamAttemptsByUserQuery, useGetExamsQuery } from '../../../services/exams';
import { useGetStudentAnalyticsQuery } from '../../../services/students';
import {
  AnalyticsBarChart,
  LineChartTooltip, PieChartLegend,
} from '../ChartComponents';
import { SORT_OPTIONS } from '../constants';
import {
  ContentContainer,
} from '../index.styles';
import {
  AnalyticsData, BaseChartData, SortableMarkType,
} from '../types';
import {
  getAverageAnalyticsDataArray,
  getMistakesAnalyticsDataArray,
  getQuestionAnalyticsDataArray,
  getTopicsAnalyticsDataArray,
  sortAnalyticsDataArray,
} from '../utils';

interface OverviewTabProps {
  subject: string,
  studentId: string,
}

const OverviewTab = ({
  subject, studentId,
}: OverviewTabProps) => {
  const theme = useTheme();
  const isSm = useMediaQuery(theme.breakpoints.up('sm'));

  const [mistakesData, setMistakesData] = useState<BaseChartData[] | undefined>();
  const [averageData, setAverageData] = useState<BaseChartData[] | undefined>();
  const [questionTypeData, setQuestionTypeData] = useState<AnalyticsData[] | undefined>();
  const [topicsData, setTopicsData] = useState<AnalyticsData[] | undefined>();
  const [sortedQuestionTypeData, setSortedQuestionTypeData] =
    useState<AnalyticsData[] | undefined>();
  const [sortedTopicsData, setSortedTopicsData] = useState<AnalyticsData[] | undefined>();
  const [examsData, setExamsData] = useState<{[key: string]: any}[] | undefined>();
  const [sortQuestionTypeBy, setSortQuestionTypeBy] =
    useState<SortableMarkType>(SORT_OPTIONS[0].key as SortableMarkType);
  const [sortTopicsBy, setSortTopicsBy] =
        useState<SortableMarkType>(SORT_OPTIONS[0].key as SortableMarkType);
  const [examTypes, setExamTypes] = useState<string[]>([]);
  const [selectedExamTypes, setSelectedExamTypes] = useState<string[]>([]);
  const [filteredExams, setFilteredExams] =
        useState<{[key: string]: any}[] | undefined>(examsData ?? []);

  useEffect(() => {
    if (examsData) {
      const uniqueExamTypes = Array.from(new Set(examsData.map((exam) => exam.type)));
      setExamTypes(uniqueExamTypes);
      setSelectedExamTypes(uniqueExamTypes);
    }
  }, [examsData]);

  const handleExamTypeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    setSelectedExamTypes((prevSelectedExamTypes) => {
      if (prevSelectedExamTypes.includes(value)) {
        return prevSelectedExamTypes.filter((type) => type !== value);
      }
      return [...prevSelectedExamTypes, value];
    });
  };

  // Filter exams data based on selected exam types
  useEffect(() => {
    if (examsData && selectedExamTypes.length > 0) {
      const filteredData = examsData
        .filter((exam) => selectedExamTypes.includes(exam.type))
        .sort((a, b) => a.creationDatetime - b.creationDatetime);
      setFilteredExams(filteredData);
    } else {
      // If no types are selected, display nothing
      setFilteredExams([]);
    }
  }, [examsData, selectedExamTypes]);

  /**
   * Categories Queries & Mutations
   */
  const { data: analyticsData,
    isFetching: isAnalyticsLoading,
    isError: isAnalyticsError } = useGetStudentAnalyticsQuery({
    studentId,
    subject,
  },
  { skip: !subject || subject === '' });

  const { data: exams, isFetching: isExamsLoading } = useGetExamsQuery(
    { subject, includeInactive: true },
    { skip: !subject || subject === '' });
  const { data: markedExams, isFetching: isMarkedExamsLoading } = useGetExamAttemptsByUserQuery({
    student: studentId, subject, marked: true,
  }, { skip: !subject || subject === '' });

  useEffect(() => {
    if (analyticsData) {
      setMistakesData(getMistakesAnalyticsDataArray(analyticsData, theme));
      setAverageData(getAverageAnalyticsDataArray(analyticsData, theme));

      setQuestionTypeData(getQuestionAnalyticsDataArray(analyticsData));
      setTopicsData(getTopicsAnalyticsDataArray(analyticsData));
    }
  }, [analyticsData]);

  useEffect(() => {
    if (questionTypeData) {
      setSortedQuestionTypeData(sortAnalyticsDataArray(questionTypeData, sortQuestionTypeBy));
    }
  }, [questionTypeData, sortQuestionTypeBy]);

  useEffect(() => {
    if (topicsData) {
      setSortedTopicsData(sortAnalyticsDataArray(topicsData, sortTopicsBy));
    }
  }, [topicsData, sortTopicsBy]);

  useEffect(() => {
    if (exams && markedExams) {
      // Map the exam attempts to add the max mark and average mark from the exam data
      const markedExamsData = markedExams?.data.map((attempt) => {
        const exam = exams?.data.find((e) => e.label === attempt.examLabel);
        return {
          ...attempt,
          ..._.pick(exam, ['label', 'shortLabel', 'type']),
          mark: (attempt.mark / attempt.maxMark) * 100,
          topMark: ((exam?.analytics.topMark ?? 0) / attempt.maxMark) * 100,
          average: ((exam?.analytics.average ?? 0) / attempt.maxMark) * 100,
        };
      });
      setExamsData(markedExamsData);
    }
  }, [exams, markedExams]);

  const renderTriangleDot = (props: any) =>
    <TriangleDot {...props} fill={theme.palette.gray.main} />;

  const renderCircleDot = (props: any) =>
    <CircleDot {...props} fill={theme.palette.primary.main} />;

  const renderStarDot = (props: any) =>
    <StarDot {...props} fill={theme.palette.mathsExt1.main} />;

  const formatExamTypeText = (type: string) => {
    const typeOption = exams?.metadata?.type?.typeOptions?.find((option: any) =>
      option.value === type);
    if (typeOption) {
      return typeOption.label;
    }
    return type; // Fallback to the original type if not matched
  };
  return (
    (isAnalyticsLoading || isExamsLoading || isMarkedExamsLoading) ? (
      <SkeletonStack />
    ) : (
      <ContentContainer>
        {analyticsData?.analytics && !isAnalyticsError && examsData && examsData.length > 0 ? (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <ContentCard variant="outlined" title="Overall Exam Statistics">
                <Grid container>
                  <Grid item xs={12}>
                    <ResponsiveContainer width="100%" height={300}>
                      <LineChart data={filteredExams}>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} />
                        <XAxis
                          dataKey="shortLabel"
                          padding={{ left: 40, right: 40 }}
                          tickLine={false}
                          tick={{ fill: theme.palette.gray.dark }}
                        />
                        <YAxis
                          tickFormatter={(value) => `${Math.round(value)}%`}
                          axisLine={false}
                          tickLine={false}
                          tick={{ fill: theme.palette.gray.dark }}
                          domain={[0, 100]}
                        />
                        <Tooltip
                          content={LineChartTooltip}
                          wrapperStyle={{ zIndex: theme.zIndex.tooltip }}
                          cursor={{
                            stroke: theme.palette.backdrop.dark,
                            strokeWidth: 80,
                            rx: 20,
                            ry: 20,
                            opacity: 0.5,
                          }}
                        />
                        <Line
                          type="monotone"
                          dataKey="average"
                          dot={renderTriangleDot}
                          activeDot={renderTriangleDot}
                          strokeWidth={0}
                          stroke={theme.palette.gray.main}
                        />
                        <Line
                          type="monotone"
                          dataKey="mark"
                          stroke={theme.palette.primary.main}
                          dot={renderCircleDot}
                          activeDot={renderCircleDot}
                        />
                        <Line
                          type="monotone"
                          dataKey="topMark"
                          dot={renderStarDot}
                          activeDot={renderStarDot}
                          strokeWidth={0}
                          stroke={theme.palette.mathsExt1.main}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </Grid>
                </Grid>
                <div>
                  {examTypes.length > 1 && (
                    <Grid container direction="row" alignItems="center" spacing={1} justifyContent="space-evenly">
                      {examTypes.map((type) => (
                        <Grid item key={type}>
                          <FormControlLabel
                            control={(
                              <Checkbox
                                checked={selectedExamTypes.includes(type)}
                                onChange={handleExamTypeChange}
                                value={type}
                              />
                            )}
                            label={formatExamTypeText(type)}
                          />
                        </Grid>
                      ))}
                    </Grid>
                  )}
                </div>
              </ContentCard>
            </Grid>
            <Grid item xs={12} md={5}>
              <ContentCard variant="outlined" title="Average Exam Mark">
                <PieChart width={200} height={200}>
                  <Pie
                    dataKey="value"
                    data={averageData}
                    cx="50%"
                    cy={90}
                    innerRadius={60}
                    outerRadius={80}
                    fill={theme.palette.backdrop.main}
                    startAngle={90}
                    endAngle={-270}
                    labelLine={false}
                    stroke="none"
                  >
                    <Label
                      value={`${Math.round(averageData?.[0]?.value ?? 0)}%`}
                      position="centerBottom"
                      className="label-top"
                      style={{
                        fontSize: '26px',
                        fontFamily: 'Poppins',
                        fontWeight: 'bold',
                      }}
                      fill={theme.palette.darkPrimary.dark}
                    />
                    <Label
                      value={`across ${markedExams?.data.length ?? 0} exam${markedExams?.data.length === 1 ? '' : 's'}`}
                      position="centerTop"
                      className="label"
                      fontSize={12}
                      style={{
                        transform: `translateY(${theme.spacing(1)})`,
                      }}
                    />
                    {
                      averageData?.map((entry) => (
                        <Cell
                          key={entry.name}
                          fill={entry.fill}
                          style={{ outline: 'none' }}
                        />
                      ))
                    }
                  </Pie>
                </PieChart>
              </ContentCard>
            </Grid>
            <Grid item xs={12} md={7}>
              <ContentCard variant="outlined" title="Mistakes Distribution">
                <PieChart width={isSm ? 325 : 300} height={200}>
                  <Pie
                    dataKey="value"
                    data={mistakesData}
                    cx="60%"
                    cy={90}
                    outerRadius={80}
                    fill={theme.palette.backdrop.main}
                    startAngle={90}
                    endAngle={-270}
                    labelLine={false}
                    stroke="none"
                  >
                    {
                      mistakesData?.map((entry) => (
                        <Cell
                          key={entry.name}
                          fill={entry.fill}
                          style={{ outline: 'none' }}
                        />
                      ))
                    }
                  </Pie>
                  <Legend
                    layout="vertical"
                    align="right"
                    verticalAlign="middle"
                    iconSize={0}
                    content={PieChartLegend}
                  />
                </PieChart>
              </ContentCard>
            </Grid>
            <Grid item xs={12}>
              <ContentCard variant="outlined">
                <AnalyticsBarChart
                  sortBy={sortQuestionTypeBy}
                  setSortBy={setSortQuestionTypeBy}
                  data={sortedQuestionTypeData}
                  category="Question Type"
                />
              </ContentCard>
            </Grid>
            <Grid item xs={12}>
              <ContentCard variant="outlined">
                <AnalyticsBarChart
                  sortBy={sortTopicsBy}
                  setSortBy={setSortTopicsBy}
                  data={sortedTopicsData}
                  category="Topic"
                />
              </ContentCard>
            </Grid>
          </Grid>
        ) : (
          <NoContentMessage />
        )}
      </ContentContainer>
    )
  );
};
export default OverviewTab;
